import { useMemo, useCallback } from 'react';
import { Dialog, Tag, Button } from '@workspace/design-system/src/components';
import { useGetPlanOrder } from '@src/apis/pay/queries';
import { PaymentCancelTossDialog } from './payment-cancel-toss-dialog';
import { PaymentCancelFormDialog } from './payment-cancel-form-dialog';
import { useDialog } from '@workspace/design-system/src/components';
import { getPlanLabel } from '../constants';
import type { OrderStatus, PaymentChannel } from '@src/apis/pay';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';

interface PlanOrderDetailModalProps {
  applicationId: string;
  orderId: string;
  onClose: () => void;
}

type PaymentStatus = '완료' | '취소' | '실패';
type PaymentProduct = '무료' | '프로' | '엔터프라이즈' | '티처' | '클래스' | '리얼월드';
type PaymentMethod = '카드 결제' | '세금계산서';

export function PlanOrderDetailModal({ applicationId, orderId, onClose }: PlanOrderDetailModalProps) {
  const { openDialog, closeDialog } = useDialog();
  
  // API 호출
  const { data: orderResponse, isLoading } = useGetPlanOrder({
    applicationId,
    orderId,
  });

  // API 응답을 UI 형식으로 변환
  const orderData = useMemo(() => {
    const order = orderResponse?.data?.data;
    if (!order) return null;

    // 결제 상태 변환
    let status: PaymentStatus = '완료';
    const orderStatus: OrderStatus = order.status;
    if (orderStatus === 'Canceled' || orderStatus === 'Refunded') {
      status = '취소';
    } else if (orderStatus === 'Failed') {
      status = '실패';
    } else if (orderStatus === 'Paid') {
      status = '완료';
    } else if (orderStatus === 'Created') {
      status = '완료'; // Created 상태도 완료로 표시
    }

    // 상품명 변환
    const product = getPlanLabel(order.productName) as PaymentProduct;

    // 결제 방식 변환
    let paymentMethod: PaymentMethod = '카드 결제';
    const paymentChannel: PaymentChannel = order.paymentChannel;
    if (paymentChannel === 'Invoice') {
      paymentMethod = '세금계산서';
    } else {
      paymentMethod = '카드 결제';
    }

    // 취소 가능 여부: Paid 상태이고 Canceled/Refunded가 아니면 취소 가능
    const canCancel = orderStatus === 'Paid';

    return {
      id: order.id,
      paymentDate: new Date(order.createdAt),
      product,
      paymentMethod,
      amount: order.price.toString(),
      status,
      requestId: order.paymentRequestId || '-',
      payerName: order.payerName || '-',
      payerPhone: order.payerPhone || '-',
      payerEmail: order.payerEmail || '-',
      cancelDate: order.canceledAt ? new Date(order.canceledAt) : null,
      canCancel,
      receiptUrl: order.receiptUrl,
    };
  }, [orderResponse]);

  // 날짜 포맷팅 함수
  const formatDate = useCallback((date: Date | null, paymentMethod?: PaymentMethod): string => {
    if (!date) return '-';

    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());

    // 세금계산서인 경우 일자까지만 표시
    if (paymentMethod === '세금계산서') {
      return `${year}-${month}-${day}`;
    }

    // 나머지는 초까지 표시
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }, []);

  // 금액 포맷팅 함수
  const formatAmount = useCallback((amount: string): string => {
    if (!amount || amount === '0') return '0원';
    const numAmount = parseInt(amount.replace(/,/g, ''), 10);
    return `${numAmount.toLocaleString()}원`;
  }, []);

  // 결제 상태 태그 스타일 매핑
  const getStatusTagProps = useCallback((status: PaymentStatus) => {
    switch (status) {
      case '완료':
        return { color: 'green' as const, text: '완료' };
      case '취소':
        return { color: 'gray' as const, text: '취소' };
      case '실패':
        return { color: 'red' as const, text: '실패' };
      default:
        return { color: 'gray' as const, text: status };
    }
  }, []);

  // 결제 취소 버튼 클릭 핸들러
  const handleCancelClick = useCallback(() => {
    if (!orderData) return;

    // 카드 결제이고 취소 가능한 경우 토스 모달 표시
    if (orderData.paymentMethod === '카드 결제' && orderData.canCancel) {
      openDialog(
        <PaymentCancelTossDialog
          onClose={closeDialog}
          onConfirm={() => {
            window.open('https://www.tosspayments.com/', '_blank');
            closeDialog();
          }}
        />
      );
    } else {
      // 그 외에는 일반 취소 모달 표시
      const variant = orderData.paymentMethod === '세금계산서' ? 'taxInvoice' : 'noPayment';
      openDialog(
        <PaymentCancelFormDialog
          variant={variant}
          onClose={closeDialog}
          onConfirm={(reason: string) => {
            console.log('결제 취소:', orderData.id, '사유:', reason);
            // TODO: 실제 API 호출
            closeDialog();
          }}
        />
      );
    }
  }, [orderData, openDialog, closeDialog]);

  // 영수증 다운로드
  const handleReceiptDownload = useCallback((receiptUrl: string) => {
    window.open(receiptUrl, '_blank');
  }, []);

  if (isLoading) {
    return (
      <Dialog>
        <Dialog.Content className="max-w-[1200px] max-h-[840px]">
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-[#F0F0F0] border-t-[#3C78F4] rounded-full animate-spin" />
              <p className="text-[14px] text-[#595959]">로딩 중...</p>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    );
  }

  if (!orderData) {
    return (
      <Dialog>
        <Dialog.Content className="max-w-[1200px] max-h-[840px]">
          <div className="px-8 py-6 border-b border-[#F0F0F0]">
            <h2 className="text-[20px] font-bold text-[#101010] leading-[26px]">결제 내역 상세 보기</h2>
          </div>
          <div className="flex items-center justify-center py-20">
            <p className="text-[14px] text-[#BFBFBF]">결제 내역을 찾을 수 없습니다.</p>
          </div>
          <div className="px-6 py-4 border-t border-[#F0F0F0] flex justify-end">
            <button
              onClick={onClose}
              className="h-10 min-w-[99px] px-6 bg-[#F0F0F0] text-[#101010] rounded-xl text-[16px] font-bold leading-[20px] hover:bg-[#E6E6E6] transition-colors"
            >
              돌아가기
            </button>
          </div>
        </Dialog.Content>
      </Dialog>
    );
  }

  const statusProps = getStatusTagProps(orderData.status);

  return (
    <Dialog>
      <Dialog.Content className="max-w-[1200px] max-h-[840px]">
        {/* 헤더 */}
        <div className="px-8 py-6 border-b border-[#F0F0F0]">
          <h2 className="text-[20px] font-bold text-[#101010] leading-[26px]">결제 내역 상세 보기</h2>
        </div>

        {/* 테이블 */}
        <div className="px-8 py-8 overflow-x-auto">
          <table className="w-full border-collapse min-w-[1600px]">
            <thead>
              <tr className="bg-[#F5F5F5]">
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 상태</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 일시</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 상품</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 방식</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">영수증</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 금액</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 요청 ID</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제자 명</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제자 연락처</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제자 이메일</th>
                <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px] whitespace-nowrap">결제 취소 일시</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#F0F0F0]">
                {/* 결제 상태 */}
                <td className="p-3 whitespace-nowrap">
                  <Tag
                    color={statusProps.color}
                    size="S-16"
                    text={statusProps.text}
                    rounded
                  />
                </td>

                {/* 결제 일시 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {formatDate(orderData.paymentDate, orderData.paymentMethod)}
                </td>

                {/* 결제 상품 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {orderData.product}
                </td>

                {/* 결제 방식 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {orderData.paymentMethod}
                </td>

                {/* 영수증 */}
                <td className="p-3 whitespace-nowrap">
                  {orderData.receiptUrl ? (
                    <button
                      onClick={() => handleReceiptDownload(orderData.receiptUrl!)}
                      className="text-[14px] text-[#0066FF] leading-[20px] tracking-[-0.1px] hover:underline cursor-pointer"
                      aria-label="영수증 다운로드"
                    >
                      영수증
                    </button>
                  ) : (
                    <span className="text-[14px] text-[#BFBFBF] leading-[20px] tracking-[-0.1px]">-</span>
                  )}
                </td>

                {/* 결제 금액 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {formatAmount(orderData.amount)}
                </td>

                {/* 결제 요청 ID */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {orderData.requestId}
                </td>

                {/* 결제자명 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {orderData.payerName}
                </td>

                {/* 결제자 연락처 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {formatPhoneNumberForDisplay(orderData.payerPhone)}
                </td>

                {/* 결제자 이메일 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {orderData.payerEmail}
                </td>

                {/* 결제 취소 일시 */}
                <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-nowrap">
                  {formatDate(orderData.cancelDate)}
                </td>              
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F0F0F0] flex justify-end">
          <button
            onClick={onClose}
            className="h-10 min-w-[99px] px-6 bg-[#F0F0F0] text-[#101010] rounded-xl text-[16px] font-bold leading-[20px] hover:bg-[#E6E6E6] transition-colors"
          >
            돌아가기
          </button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

